export const ALLOWED_VARIABLES = [
    "property_address", "property_address2", "property_address_city", "property_address_state", "property_address_zip",
    "owner_address", "owner_address2", "owner_address_city", "owner_address_state", "owner_address_zip",
    "owner_name", "owner_firstname", "owner_lastname",
    "signature_name", "signature_sign_off", "signature_company", "signature_phone", "signature_email", "other_contact",
    "return_address", "return_address2", "return_address_city", "return_address_state", "return_address_zip",
    "disclosure",
];

const isWord = (s) => /^[A-Za-z0-9_]+$/.test(s);
const isWordChar = (ch) => /[A-Za-z0-9_]/.test(ch);

/**
 * Returns precise ranges:
 *   validRanges:   [{ start, end }]
 *   invalidRanges: [{ start, end, reason }]
 * end is exclusive.
 */
export function validateVariableTags(text) {
    const validRanges = [];
    const invalidRanges = [];

    if (!text || typeof text !== "string") {
        return { validRanges, invalidRanges, hasErrors: false };
    }

    const len = text.length;
    const consumed = new Array(len).fill(false);
    const markRange = (s, e) => { for (let i = s; i < e; i++) consumed[i] = true; };

    let i = 0;
    while (i < len) {
        if (consumed[i]) { i++; continue; }

        const ch = text[i];

        // ---- Double-brace open '{{'
        if (ch === "{" && i + 1 < len && text[i + 1] === "{" && !consumed[i + 1]) {
            const start = i;

            // Nearest occurrences after this open
            const nextOpen = text.indexOf("{{", i + 2);
            const nextClose = text.indexOf("}}", i + 2);

            // Helper: end index right after the variable token prefix '{{name'
            let nameEnd = i + 2;
            while (nameEnd < len && isWordChar(text[nameEnd])) nameEnd++;

            // CASE A: Another '{{' appears before the next '}}'  → treat as unclosed
            if (nextClose === -1 || (nextOpen !== -1 && nextOpen < nextClose)) {
                // Mark ONLY the token itself '{{name' (not spaces/newlines)
                const end = Math.max(nameEnd, i + 2); // ensure at least '{{'
                invalidRanges.push({ start, end, reason: "Unclosed {{ }}." });
                markRange(start, end);
                i = end;
                continue;
            }

            // We have a closing '}}' before the next open
            const close = nextClose;
            const end = close + 2;           // include '}}'
            const inner = text.slice(i + 2, close);

            if (inner.length === 0) {
                invalidRanges.push({ start, end, reason: "Empty variable name." });
                markRange(start, end); i = end; continue;
            }
            if (/\s/.test(inner)) {
                invalidRanges.push({ start, end, reason: "Variable name cannot contain spaces." });
                markRange(start, end); i = end; continue;
            }
            if (!isWord(inner)) {
                invalidRanges.push({ start, end, reason: "Only letters, numbers, and underscore are allowed." });
                markRange(start, end); i = end; continue;
            }
            if (!ALLOWED_VARIABLES.includes(inner)) {
                invalidRanges.push({ start, end, reason: `"${inner}" is not a recognized variable.` });
                markRange(start, end); i = end; continue;
            }

            // Valid variable
            validRanges.push({ start, end });
            markRange(start, end);
            i = end;
            continue;
        }

        // ---- Lone '{' or single-brace pair
        if (ch === "{" && text[i + 1] !== "{") {
            const s = i;
            const close = text.indexOf("}", i + 1);
            if (close !== -1) {
                invalidRanges.push({ start: s, end: close + 1, reason: "Use double braces {{ }} for variables." });
                markRange(s, close + 1);
                i = close + 1;
            } else {
                let j = i + 1;
                while (j < len && !/\s/.test(text[j])) j++;
                invalidRanges.push({ start: s, end: j, reason: 'Lone "{". Use {{ }}.' });
                markRange(s, j);
                i = j;
            }
            continue;
        }

        // ---- Stray closing braces
        if (ch === "}") {
            if (i + 1 < len && text[i + 1] === "}") {
                invalidRanges.push({ start: i, end: i + 2, reason: "Unmatched closing braces." });
                markRange(i, i + 2);
                i += 2;
            } else {
                invalidRanges.push({ start: i, end: i + 1, reason: 'Lone "}".' });
                markRange(i, i + 1);
                i += 1;
            }
            continue;
        }

        i++;
    }

    return {
        validRanges,
        invalidRanges,
        hasErrors: invalidRanges.length > 0
    };
}
